import { cn } from "@/lib/utils";

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  /** Section boundaries — array of step counts per section. E.g. [2, 2, 2] for 3 sections with 2 steps each. */
  sections?: number[];
}

export function ProgressBar({
  currentStep,
  totalSteps,
  sections,
}: ProgressBarProps) {
  // Legacy mode: no sections, same as before
  if (!sections) {
    return (
      <div className="flex gap-1.5">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-0.5 flex-1 rounded-full transition-all duration-300",
              i <= currentStep ? "bg-aubergine-700" : "bg-neutral-200",
            )}
          />
        ))}
      </div>
    );
  }

  // Section mode: each segment fills proportionally
  let stepsSoFar = 0;
  return (
    <div className="flex gap-1.5">
      {sections.map((stepsInSection, sectionIndex) => {
        const sectionStart = stepsSoFar;
        stepsSoFar += stepsInSection;
        const sectionEnd = stepsSoFar;

        const isComplete = currentStep >= sectionEnd;
        const isActive =
          currentStep >= sectionStart && currentStep < sectionEnd;
        const progress = isComplete
          ? 100
          : isActive
            ? ((currentStep - sectionStart + 1) / stepsInSection) * 100
            : 0;

        return (
          <div
            key={sectionIndex}
            className="h-0.5 flex-1 rounded-full bg-neutral-200 overflow-hidden"
          >
            <div
              className="h-full rounded-full bg-aubergine-700 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}
